import to from 'await-to-js'
import { TableTypeSlug } from 'models/companies'
import { clientInstance } from 'utils/http-client'
import { notifyError } from './toast'
import { baseAPICall } from './baseAPICall'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from './queryClient'
import {
  CHAIN_SUMMARY_KEY,
  DEAL_TABLE_KEY,
  CONTACTS_TABLE_KEY,
  LODGING_TABLE_KEY,
  LODGING_DOOR_TABLE_KEY,
} from 'constants/tableQueryKeys'
import { Campaign } from 'models/campaign'
import { ChainListResponse } from 'models/chains'
import {
  CHAINS_TABLE_KEY,
  ALL_CAMPAIGNS_TABLE_KEY,
  CHAINS_REPORTS_TABLE_KEY,
  COLLEGE_AND_UNIVERSITY_TABLE_KEY,
  DOOR_SUMMARY_KEY,
  DOORS_TABLE_KEY,
  DOORS_REPORTS_TABLE_KEY,
  getCampaignTableSuffix,
  HOSPITALS_TABLE_KEY,
  NURSING_HOMES_TABLE_KEY,
  K12_TABLE_KEY,
  UGC_TABLE_KEY,
} from 'constants/tableQueryKeys'
import lodash from 'lodash'
import { downloadFile } from 'utils/csvDownloader'
import { adaptUnifiedListParams } from './opportunities/unifiedResponseAdapter'
import { UnifiedTargetType } from './opportunities/targetTypeMap'

interface CommonTableActionParams {
  include_ids: number[]
  exclude_ids: number[]
  filter_params: Record<string, unknown>
}

/**
 * Get the endpoint for a table type
 * @param tableTypeSlug - The table type slug
 * @param endpoint - The endpoint
 * @param args - The arguments to be formatted into the full result (path + endpoint). This will only be used if the string contains a % character.
 * @returns The formatted endpoint
 */
function getEndpointForTableType(
  tableTypeSlug: TableTypeSlug,
  endpoint: string,
  ...args: string[]
): string {
  function safeFormat(str: string, ...args: string[]) {
    if (!str.includes('%')) return str
    let i = 0
    return str.replace(/%/g, () => args[i++] ?? '%')
  }

  const endpointCompanyTypeMap: Partial<Record<TableTypeSlug, string>> = {
    'restaurants-bars': 'restaurants/chains',
    'restaurants-bars-doors': 'restaurants/places',
    'education-cu': 'universities',
    'education-k-12': 'k12/districts',
    'healthcare-hospitals': 'hospital',
    'healthcare-nursinghomes': 'nursing_home',
    'contacts-table': 'contacts',
    'deals-table': 'contacts/deals', // Used for list, detail, bulk operations
    'deals-table-reports': 'reports/deals',
    'lodging-hospitality': 'places/lodging',
    'lodging-hospitality-doors': 'places/lodging-doors',
    convenience: 'places/convenience',
    'convenience-doors': 'places/convenience-doors',
    'deals-pipeline-table': 'reports/dashboard/deals-pipeline',
    'companies-table': 'contacts/contactcompanies',
    'campaigns-table': 'contacts/campaigns',
    'activity-table': 'activity/v2/team',
    'activity-my-table': 'activity/v2/my',
    'rebate-claims-table': 'tradespend/firstbite/claims',
    'rebate-offers-table': 'tradespend/firstbite/offers',
    'unmatched-doors-table': 'contacts/doorproxies',
    'doors-reports-table': 'reports/doors',
    'chains-reports-table': 'reports/chains',
  }

  const prefix =
    endpointCompanyTypeMap[tableTypeSlug] ?? 'contacts/contactcompanies'
  const url = `${prefix}${endpoint}`
  if (args.length > 0) {
    return safeFormat(url, ...args)
  }
  return url
}

/**
 * Invalidate every query scoped to a campaign after its target membership
 * changes (bulk assign/unassign, contact import).
 *
 * Covers both key families: campaign-scoped tables, whose first key element
 * ends with `getCampaignTableSuffix(campaignId)` (targets table for every
 * company type plus campaign contacts/deals), and `['campaign', <id>, ...]`
 * metadata queries (tab counts, zero-state gates, contact requests, lead
 * review). Must stay exhaustive: refetchOnMount is disabled globally, so any
 * missed query serves stale data until a hard refresh (FIR-7460).
 * `refetchType: 'all'` because these queries usually live on a different page
 * than the table the action ran on.
 */
export function invalidateCampaignTargetQueries(campaignId: number) {
  const campaignTableSuffix = getCampaignTableSuffix(campaignId)
  void queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === 'string' &&
      query.queryKey[0].endsWith(campaignTableSuffix),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: ['campaign', campaignId],
    exact: false,
    refetchType: 'all',
  })
}

/* -------------
Assign To Campaign
-------------- */
// Refer to server/common/table_actions_mixins/BulkAssignToCampaignMixin.py for the server side implementation

/**
 * Build the unified `/opportunities/` endpoint + adapted query params for a bulk
 * action, or return `null` when not routing to unified (caller keeps legacy).
 *
 * Under the unified-opps flag the targets tables render rows from
 * `/opportunities/` (chains) / `/opportunities/doors/` (locations), so a
 * *select-all* action (empty `include_ids`) must be re-resolved by that SAME
 * engine — otherwise the server re-resolves the filters through the legacy
 * engine and the action hits the wrong (tiny) set (the FIR-7822 prod bug). When
 * `unifiedTargetType` is set we point the action at the unified endpoint and
 * send the list's adapted params (incl. `target_type`) so the resolved set
 * matches the rows the user saw. `unifiedDoors` selects the doors-scoped
 * endpoint variant. `unifiedSuffix` is the path after the surface prefix, e.g.
 * `campaigns/assign/` or `bulk/tags/`.
 */
function unifiedActionOverride(
  unifiedSuffix: string,
  filterParams: Record<string, unknown>,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
): { url: string; params: Record<string, unknown> } | null {
  if (!unifiedTargetType) {
    return null
  }
  const base = unifiedDoors ? 'opportunities/doors/' : 'opportunities/'
  return {
    url: `${base}${unifiedSuffix}`,
    params: {
      ...adaptUnifiedListParams(filterParams, unifiedTargetType),
      target_type: unifiedTargetType,
    },
  }
}

/**
 * Resolve the endpoint + query params for a campaign assign/unassign call,
 * routing to the unified endpoint when `unifiedTargetType` is set (see
 * {@link unifiedActionOverride}), else the legacy per-vertical endpoint.
 */
function resolveCampaignActionRequest(
  companyType: TableTypeSlug,
  filterParams: Record<string, unknown>,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
): { url: string; params: Record<string, unknown> } {
  return (
    unifiedActionOverride(
      'campaigns/assign/',
      filterParams,
      unifiedTargetType,
      unifiedDoors
    ) ?? {
      url: getEndpointForTableType(companyType, '/campaigns/assign/'),
      params: filterParams,
    }
  )
}

/**
 * Resolve the endpoint + query params for a distributors/gpos membership action,
 * shared by {@link commonManageDistributors} and {@link commonManageGPOs}.
 *
 * Routes to the unified endpoint when `unifiedTargetType` is set (see
 * {@link unifiedActionOverride}), else the per-vertical `legacyUrl`. The UGC
 * companies table (never unified) carries its `sub_category_id` as a query param.
 */
function resolveMembershipActionRequest(
  unifiedSuffix: string,
  legacyUrl: string,
  filterParams: Record<string, unknown>,
  routing: { unifiedTargetType?: UnifiedTargetType; unifiedDoors?: boolean },
  companies: { isCompaniesTable: boolean; subCategoryId?: number }
): { url: string; params: Record<string, unknown> } {
  const override = unifiedActionOverride(
    unifiedSuffix,
    filterParams,
    routing.unifiedTargetType,
    routing.unifiedDoors
  )
  const url = override?.url ?? legacyUrl
  const params = override?.params ?? filterParams
  if (companies.isCompaniesTable && !override) {
    params['sub_category_id'] = companies.subCategoryId
  }
  return { url, params }
}

type AssignToCampaignRequest = {
  campaign_id: number
  campaign_name: string
  campaign_color: string
  target_type_slug?: string
  clear_type?: boolean
} & CommonTableActionParams

async function assignToCampaign(
  companyType: TableTypeSlug,
  body: AssignToCampaignRequest,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
) {
  const { url, params } = resolveCampaignActionRequest(
    companyType,
    body.filter_params,
    unifiedTargetType,
    unifiedDoors
  )
  return baseAPICall(
    () =>
      clientInstance.post(
        url,
        {
          campaign_id: body.campaign_id,
          include_ids: body.include_ids,
          exclude_ids: body.exclude_ids,
          assign: true,
          target_type_slug: body.target_type_slug ?? '',
          clear_type: body.clear_type ?? false,
        },
        {
          params,
        }
      ),
    {
      successMessage: 'Succesfully assigned to campaign',
    }
  )
}

export const useAssignToCampaignMutation = (
  oppsListQueryKey: string[],
  chainProxyIdAccessor = 'id',
  companyTypeSlug: TableTypeSlug,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
) => {
  return useMutation({
    mutationFn: (variables: AssignToCampaignRequest) =>
      assignToCampaign(
        companyTypeSlug,
        variables,
        unifiedTargetType,
        unifiedDoors
      ),
    onMutate: async (newCampaign) => {
      if (!oppsListQueryKey) {
        return
      }

      // Base table
      await queryClient.cancelQueries({ queryKey: oppsListQueryKey })
      const prev = queryClient.getQueryData(oppsListQueryKey) as {
        results: any[]
      }
      queryClient.setQueryData(oppsListQueryKey, (old: ChainListResponse) => {
        const data = structuredClone(old)

        newCampaign.include_ids?.forEach((id) => {
          const row = data.results.find(
            (d) => lodash.get(d, chainProxyIdAccessor) === id
          )
          if (row) {
            if (
              !row.campaigns?.some(
                (cmp) => cmp.name === newCampaign.campaign_name
              )
            ) {
              row.campaigns = [
                ...(row.campaigns || []),
                {
                  id: newCampaign.campaign_id,
                  name: newCampaign.campaign_name,
                  color: newCampaign.campaign_color,
                } as Campaign,
              ]
            }
          }
        })

        return data
      })

      // No optimistic update for the campaign-scoped tables: they are never
      // rendered on the page the assign action runs from, and the campaign
      // table key prefix also matches sibling queries with different shapes
      // (e.g. `[..., 'summary']`), which a blind results-push corrupted and
      // could even crash, aborting the mutation before the request was sent
      // (FIR-7460). onSettled invalidates them instead.

      return { prev }
    },
    onError: (_, __, context) => {
      // Restore base table
      queryClient.setQueryData(oppsListQueryKey, context?.prev)
    },
    onSettled: async (_, __, variables) => {
      // note: the reason we invalidate the query rather than use optimistic ui
      // is because if we remove all rows in the current page, we need to
      // fetch from the server

      // Invalidate every campaign-scoped query (targets/contacts/deals tables,
      // tab counts, zero-state gates); see invalidateCampaignTargetQueries
      invalidateCampaignTargetQueries(variables.campaign_id)

      // Invalidate the chain summary (used in select all dropdown)
      void queryClient.invalidateQueries({
        queryKey: [CHAIN_SUMMARY_KEY],
        exact: false,
        refetchType: 'all',
      })

      // Invalidate campaigns list: type may have changed via inference
      void queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
        refetchType: 'all',
      })
    },
  })
}

/* -------------
Unassign From Campaign
-------------- */
type UnassignFromCampaignRequest = {
  campaign_id: number
} & CommonTableActionParams

async function unassignFromCampaign(
  companyType: TableTypeSlug,
  body: UnassignFromCampaignRequest,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
) {
  const { url, params } = resolveCampaignActionRequest(
    companyType,
    body.filter_params,
    unifiedTargetType,
    unifiedDoors
  )
  return baseAPICall(
    () =>
      clientInstance.post(
        url,
        {
          campaign_id: body.campaign_id,
          include_ids: body.include_ids,
          exclude_ids: body.exclude_ids,
          assign: false,
        },
        {
          params,
        }
      ),
    {
      successMessage: 'Succesfully unassigned from campaign',
    }
  )
}

export const useUnassignToCampaignMutation = (
  companyType: TableTypeSlug,
  oppsListQueryKey: string[],
  chainProxyIdAccessor = 'id',
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
) => {
  return useMutation({
    mutationFn: (variables: UnassignFromCampaignRequest) =>
      unassignFromCampaign(
        companyType,
        variables,
        unifiedTargetType,
        unifiedDoors
      ),
    onMutate: async (newCampaign) => {
      if (!oppsListQueryKey) {
        return
      }
      await queryClient.cancelQueries({ queryKey: oppsListQueryKey })

      const prev = queryClient.getQueryData(oppsListQueryKey)

      queryClient.setQueryData(oppsListQueryKey, (old: any) => {
        const data = structuredClone(old)

        if (
          newCampaign.include_ids.length === 0 &&
          newCampaign.exclude_ids.length === 0
        ) {
          data.results = []
          return data
        }

        const results = data.results.filter(
          (it: any) =>
            !newCampaign.include_ids?.includes(
              lodash.get(it, chainProxyIdAccessor)
            )
        )

        data.results = results

        return data
      })

      return { prev }
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(oppsListQueryKey, context?.prev)
    },
    onSettled: async (_, __, variables) => {
      // note: the reason we invalidate the query rather than use optimistic ui
      // is because if we remove all rows in the current page, we need to
      // fetch from the server

      // Invalidate the table the action ran on (the campaign targets table;
      // unassign is only offered there)
      void queryClient.invalidateQueries({
        queryKey: oppsListQueryKey,
        exact: false,
      })

      // Invalidate every campaign-scoped query (targets/contacts/deals tables,
      // tab counts, zero-state gates); see invalidateCampaignTargetQueries
      invalidateCampaignTargetQueries(variables.campaign_id)

      // Invalidate the chain summary (used in select all dropdown)
      void queryClient.invalidateQueries({
        queryKey: [CHAIN_SUMMARY_KEY],
        exact: false,
      })

      // Invalidate campaigns list: mutation symmetry with assign
      void queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
        refetchType: 'all',
      })
    },
  })
}

/* -------------
Create Deals
-------------- */
// Refer to server/common/table_actions_mixins/BulkCreateDealsMixin.py for the server side implementation
export async function commonCreateDeals(
  slug: TableTypeSlug,
  options: {
    deals: {
      sales_stage_id?: number | null
      account_owner_id?: number | null
      product_id?: number
      product_variant_id?: number | null
      origin_campaign_id?: number | null
      monthly_volume_override?: number | null
      monthly_revenue_override?: number | null
      close_date_override?: string | null
      name?: string | null
    }[]
    apply_to?: string
    door_ids?: number[]
    door_proxy_id?: number
    unifiedTargetType?: UnifiedTargetType
    unifiedDoors?: boolean
  } & CommonTableActionParams
) {
  // Routing params are not request data — keep them out of the body so they
  // can never leak onto the wire (the body below is already explicit; this also
  // future-proofs against a refactor that spreads `options`).
  const { unifiedTargetType, unifiedDoors, ...dealOptions } = options
  const override = unifiedActionOverride(
    'bulk/deals/',
    dealOptions.filter_params,
    unifiedTargetType,
    unifiedDoors
  )
  const url = override?.url ?? getEndpointForTableType(slug, '/bulk/deals/')

  const [err, res] = await to(
    clientInstance.post(
      url,
      {
        include_ids: dealOptions.include_ids,
        exclude_ids: dealOptions.exclude_ids,
        deals: dealOptions.deals,
        apply_to: dealOptions.apply_to,
        door_ids: dealOptions.door_ids,
        door_proxy_id: dealOptions.door_proxy_id,
      },
      {
        params: override?.params ?? dealOptions.filter_params,
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  return res
}

/* -------------
Manage Tags
-------------- */
export type ManageTagsDistributorsParams = {
  items: number[]
  action: 'add' | 'delete'
  door_proxy_ids?: number[]
} & CommonTableActionParams

export async function commonManageTags({
  values,
  tableType: companyType,
  isContactTags = false,
  unifiedTargetType,
  unifiedDoors,
}: {
  values: ManageTagsDistributorsParams
  tableType: TableTypeSlug
  isContactTags?: boolean
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
}) {
  const { filter_params, ...rest } = values

  // Contact tags are not a unified-opps action — only the chain/door tag
  // endpoints route to unified.
  const override = isContactTags
    ? null
    : unifiedActionOverride(
        'bulk/tags/',
        filter_params ?? {},
        unifiedTargetType,
        unifiedDoors
      )
  const endpoint = isContactTags ? '/bulk/contacttags/' : '/bulk/tags/'
  const url = override?.url ?? getEndpointForTableType(companyType, endpoint)

  const queryParams = override?.params ?? filter_params ?? {}

  // add company_type_slug to the request

  const [err, res] = await to(
    clientInstance.post(
      url,
      { ...rest },
      {
        params: queryParams,
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  // Invalidate corresponding queries
  const queryKeysMap: Record<string, string[]> = {
    'restaurants-bars': [
      CHAINS_TABLE_KEY,
      DOORS_TABLE_KEY,
      DOOR_SUMMARY_KEY,
      CHAIN_SUMMARY_KEY,
    ],
    'education-cu': [COLLEGE_AND_UNIVERSITY_TABLE_KEY],
    'education-k-12': [K12_TABLE_KEY],
    'healthcare-hospitals': [HOSPITALS_TABLE_KEY],
    'healthcare-nursinghomes': [NURSING_HOMES_TABLE_KEY],

    companies: [UGC_TABLE_KEY],
    companies_from_contacts: [CONTACTS_TABLE_KEY],
    'contacts-table': [CONTACTS_TABLE_KEY],
    deals: [DEAL_TABLE_KEY],
    'lodging-hospitality': [LODGING_TABLE_KEY],
    'lodging-hospitality-doors': [LODGING_DOOR_TABLE_KEY],
    'unmatched-doors-table': [DOORS_TABLE_KEY, UGC_TABLE_KEY],
    'doors-reports-table': [DOORS_REPORTS_TABLE_KEY],
    'chains-reports-table': [CHAINS_REPORTS_TABLE_KEY],
  }

  if (companyType in queryKeysMap) {
    queryKeysMap[companyType].forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key], exact: false })
    })
  }

  return res
}

/* -------------
Manage Deal Tags
-------------- */
export async function commonManageDealTags({
  values,
}: {
  values: ManageTagsDistributorsParams
}) {
  const url = 'contacts/deals/bulk/deal-tags/'

  const { filter_params, ...rest } = values

  const queryParams = filter_params ?? {}

  const [err, res] = await to(
    clientInstance.post(
      url,
      { ...rest },
      {
        params: queryParams,
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  queryClient.invalidateQueries({ queryKey: [DEAL_TABLE_KEY], exact: false })

  return res
}

/* -------------
Manage Distributors
-------------- */
export async function commonManageDistributors({
  tableType: type,
  values,
  subCategoryId,
  unifiedTargetType,
  unifiedDoors,
}: {
  tableType: TableTypeSlug
  values: ManageTagsDistributorsParams
  subCategoryId?: number
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
}) {
  const urlMap: Partial<Record<TableTypeSlug, string>> = {
    'restaurants-bars-doors': 'restaurants/places/bulk/distributors/',
    'restaurants-bars': 'restaurants/chains/bulk/distributors/',
    'deals-table': 'contacts/deals/bulk/distributors/',
    'deals-table-reports': 'reports/deals/bulk/distributors/',
    'education-cu': 'universities/bulk/distributors/',
    'education-k-12': 'k12/districts/bulk/distributors/',
    'healthcare-hospitals': 'hospital/bulk/distributors/',
    'healthcare-nursinghomes': 'nursing_home/bulk/distributors/',
    'lodging-hospitality': 'places/lodging/bulk/distributors/',
    'lodging-hospitality-doors': 'places/lodging-doors/bulk/distributors/',
    'companies-table': `contacts/contactcompanies/bulk/distributors/`,
    'unmatched-doors-table': 'contacts/doorproxies/bulk/distributors/',
    'doors-reports-table': 'reports/doors/bulk/distributors/',
    'chains-reports-table': 'reports/chains/bulk/distributors/',
  }

  const { filter_params, ...rest } = values
  const { url, params: queryParams } = resolveMembershipActionRequest(
    'bulk/distributors/',
    urlMap[type] ?? (urlMap['companies-table'] as string),
    filter_params ?? {},
    { unifiedTargetType, unifiedDoors },
    { isCompaniesTable: type === 'companies-table', subCategoryId }
  )

  const [err, res] = await to(
    clientInstance.post(url, rest, {
      params: queryParams,
    })
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  // Invalidate corresponding queries
  const queryKeysMap: Record<string, string[]> = {
    doors: [
      CHAINS_TABLE_KEY,
      DOORS_TABLE_KEY,
      DOOR_SUMMARY_KEY,
      CHAIN_SUMMARY_KEY,
    ],
    chains: [
      CHAINS_TABLE_KEY,
      DOORS_TABLE_KEY,
      DOOR_SUMMARY_KEY,
      CHAIN_SUMMARY_KEY,
    ],
    companies: [UGC_TABLE_KEY],
    deals: [DEAL_TABLE_KEY],
    universities: [COLLEGE_AND_UNIVERSITY_TABLE_KEY],
    k12: [K12_TABLE_KEY],
    hospitals: [HOSPITALS_TABLE_KEY],
    nursing_homes: [NURSING_HOMES_TABLE_KEY],
    lodging: [LODGING_TABLE_KEY],
    'unmatched-doors-table': [DOORS_TABLE_KEY, UGC_TABLE_KEY],
    'doors-reports-table': [DOORS_REPORTS_TABLE_KEY],
    'chains-reports-table': [CHAINS_REPORTS_TABLE_KEY],
  }
  if (type in queryKeysMap) {
    queryKeysMap[type].forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] })
    })
  }

  return res
}

/* -------------
Manage GPOs
-------------- */
export async function commonManageGPOs({
  tableType: type,
  values,
  subCategoryId,
  unifiedTargetType,
  unifiedDoors,
}: {
  tableType: TableTypeSlug
  values: ManageTagsDistributorsParams
  subCategoryId?: number
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
}) {
  const urlMap: Partial<Record<TableTypeSlug, string>> = {
    'restaurants-bars-doors': 'restaurants/places/bulk/gpos/',
    'restaurants-bars': 'restaurants/chains/bulk/gpos/',
    'deals-table': 'contacts/deals/bulk/gpos/',
    'deals-table-reports': 'reports/deals/bulk/gpos/',
    'education-cu': 'universities/bulk/gpos/',
    'education-k-12': 'k12/districts/bulk/gpos/',
    'healthcare-hospitals': 'hospital/bulk/gpos/',
    'healthcare-nursinghomes': 'nursing_home/bulk/gpos/',
    'lodging-hospitality': 'places/lodging/bulk/gpos/',
    'lodging-hospitality-doors': 'places/lodging-doors/bulk/gpos/',
    'companies-table': `contacts/contactcompanies/bulk/gpos/`,
    'unmatched-doors-table': 'contacts/doorproxies/bulk/gpos/',
    'doors-reports-table': 'reports/doors/bulk/gpos/',
    'chains-reports-table': 'reports/chains/bulk/gpos/',
  }

  const { filter_params, ...rest } = values
  const { url, params: queryParams } = resolveMembershipActionRequest(
    'bulk/gpos/',
    urlMap[type] ?? (urlMap['companies-table'] as string),
    filter_params ?? {},
    { unifiedTargetType, unifiedDoors },
    { isCompaniesTable: type === 'companies-table', subCategoryId }
  )

  const [err, res] = await to(
    clientInstance.post(url, rest, {
      params: queryParams,
    })
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  return res
}

/* -------------
Export Table
-------------- */
export type CommonExportTableOptions = {
  include_ids: number[]
  exclude_ids: number[]
  filter_params: Record<string, unknown>
  selected_columns: string[]
  export_type: 'csv' | 'xlsx'
}

function getDataTypeNameForTableType(type: TableTypeSlug) {
  const dataTypeNameMap: Partial<Record<TableTypeSlug, string>> = {
    'lodging-hospitality': 'Lodging & Hospitality',
    'lodging-hospitality-doors': 'Lodging & Hospitality Doors',
    'healthcare-hospitals': 'Hospitals',
    'healthcare-nursinghomes': 'Senior Living',
    'education-cu': 'Universities',
    'education-k-12': 'K-12 Districts',
    'deals-pipeline-table': 'Deals Pipeline',
    'campaigns-table': 'Campaigns',
    'activity-table': 'Activity',
    'restaurants-bars-doors': 'Restaurants & Bars Doors',
    'restaurants-bars': 'Restaurants & Bars',
    'deals-table': 'Deals',
    'deals-table-reports': 'Deals',
    'companies-table': 'Companies',
    'contacts-table': 'Contacts',
    'rebate-claims-table': 'Rebate Claims',
    'rebate-offers-table': 'Rebate Offers',
    convenience: 'Convenience Stores',
    'convenience-doors': 'Convenience Stores -  Doors',
    'doors-reports-table': 'Doors CRM Reports',
    'chains-reports-table': 'Chains CRM Reports',
  }
  return dataTypeNameMap[type] ?? 'Unknown'
}

export async function commonExportTable(
  type: TableTypeSlug,
  options: CommonExportTableOptions,
  abortController?: AbortController,
  overrideDataTypeName?: string,
  endpointArgs?: string[]
) {
  const url = getEndpointForTableType(
    type,
    '/export/',
    ...(endpointArgs ? endpointArgs : [])
  )

  const [err, res] = await to(
    clientInstance.post(
      url,
      {
        selected_columns: options.selected_columns,
        export_type: options.export_type,
        include_ids: options.include_ids,
        exclude_ids: options.exclude_ids,
      },
      {
        params: options.filter_params,
        signal: abortController?.signal,
        responseType: 'blob',
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  downloadFile(
    res.data,
    `First Bite Export - ${overrideDataTypeName ?? getDataTypeNameForTableType(type)} - ${new Date().toLocaleDateString()}.${options.export_type}`
  )
}

/* -------------
GET City States
-------------- */
async function commonGetCityStates(companyType: TableTypeSlug) {
  const url = getEndpointForTableType(companyType, '/citystates')
  return baseAPICall<{ value: string; label: string }[]>(() =>
    clientInstance.get(url)
  )
}

export const useCommonGetCityStates = (companyType: TableTypeSlug) => {
  return useQuery({
    queryKey: ['citystates', companyType],
    queryFn: () => commonGetCityStates(companyType),
  })
}

/* -------------
GET City States - Paginated
-------------- */
export interface CityStatesParams {
  search?: string
  state?: string // Comma-separated state codes
  page_size?: number
}

export interface CityStatesPaginatedResponse {
  results: { value: string; label: string }[]
  count: number
  next: string | null
  previous: string | null
}

async function commonGetCityStatesPaginated(
  companyType: TableTypeSlug,
  params: CityStatesParams & { page?: number }
) {
  const url = getEndpointForTableType(companyType, '/citystates')
  return baseAPICall<CityStatesPaginatedResponse>(() =>
    clientInstance.get(url, { params })
  )
}

export const useCommonGetCityStatesInfinite = (
  companyType: TableTypeSlug,
  params: CityStatesParams = {},
  enabled = true
) => {
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['citystates-infinite', companyType, params],
    queryFn: ({ pageParam }) =>
      commonGetCityStatesPaginated(companyType, { ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined
      const pageStr = new URL(lastPage.next).searchParams.get('page')
      return pageStr ? parseInt(pageStr, 10) : undefined
    },
    enabled,
  })
}
